import React from 'react'
import { Modal, Table, Tag } from '@surecompanies/core_components'
import { Button } from '@sureco/design-system'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'

interface PendingIgnoreEntry {
  censusRowId: number
  fieldName: string
  ignoredNewValue: string | number | boolean
  displayEmployeeId: string
  displayName: string
  liveValue?: string
}

interface ConfirmCellChangesModalProps {
  visible: boolean
  onCancel: () => void
  onConfirm: () => void
  loading: boolean
  dirtyRows: CensusFileRowGridData[]
  updateMode: boolean
  /** When false, the user cannot dismiss the modal. Defaults to true. */
  closable?: boolean
  pendingIgnoreCreates?: PendingIgnoreEntry[]
}

const ConfirmCellChangesModal = ({
  visible,
  onCancel,
  onConfirm,
  loading,
  dirtyRows,
  updateMode,
  closable = true,
  pendingIgnoreCreates,
}: ConfirmCellChangesModalProps) => {
  const modalColumns = [
    {
      title: 'Employee ID',
      dataIndex: 'employeeId',
      key: 'employeeId',
      width: 120,
    },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 120 },
    {
      title: 'Field Changed',
      dataIndex: 'field',
      key: 'field',
      width: 180,
      render: (v: string, record: any) =>
        record.isIgnored ? (
          <span>
            {v} <Tag color="warning">Ignored</Tag>
          </span>
        ) : (
          v
        ),
    },
    {
      title: 'Original',
      dataIndex: 'beforeValue',
      key: 'beforeValue',
      width: 180,
      render: (v: string | number | boolean) => (v === '' ? '(empty)' : v),
    },
    {
      title: 'New Value',
      dataIndex: 'afterValue',
      key: 'afterValue',
      width: 180,
    },
  ]

  const editDataSource = dirtyRows.flatMap((row) => {
    if (updateMode) {
      return Object.entries(row.dirtyFields || {}).map(([field]) => ({
        key: `${row.rowId}-${field}`,
        employeeId: row.employeeId || '',
        name: `${row.employeeName}`,
        field: row.field,
        beforeValue: row.proposedInitialValue,
        afterValue: row.newValue,
        isIgnored: false,
      }))
    }

    return Object.entries(row.dirtyFields || {}).map(([field]) => ({
      key: `${row.id}-${field}`,
      employeeId: row.CompanyEmployeeId || '',
      name: `${row.firstName} ${row.lastName}`,
      field,
      beforeValue: row.rowData[field],
      afterValue: row[field],
      isIgnored: false,
    }))
  })

  const ignoreDataSource = (pendingIgnoreCreates ?? []).map((p) => ({
    key: `ignore-${p.censusRowId}-${p.fieldName}`,
    employeeId: p.displayEmployeeId,
    name: p.displayName,
    field: p.fieldName,
    beforeValue: p.liveValue ?? '-',
    afterValue: '-',
    isIgnored: true,
  }))

  const modalDataSource = [...editDataSource, ...ignoreDataSource]

  return (
    <Modal
      title="Confirm Changes"
      open={visible}
      onCancel={onCancel}
      maskClosable={closable}
      closable={closable}
      width={1000}
      footer={[
        <div
          key="footer"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Button
            type="outline"
            variant="danger"
            onClick={onCancel}
            disabled={!closable}
            size="large"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            loading={loading}
            variant="primary"
            size="large"
          >
            Save Changes
          </Button>
        </div>,
      ]}
    >
      <p>Review the changes before saving:</p>
      <Table
        scroll={{ x: 'max-content', y: 400 }}
        columns={modalColumns}
        dataSource={modalDataSource}
        pagination={false}
      />
    </Modal>
  )
}

export default ConfirmCellChangesModal
